/**
 * Parse and validate email lists for campaign invite UIs (sponsor / claim).
 * Matches edge-function `EMAIL_RE` in start-sponsor-invite-campaign.
 */
export const CAMPAIGN_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Split comma, semicolon, or newline-separated addresses (deduped, lowercased).
 */
export function parseCampaignEmailAddresses(raw: string): string[] {
  const parts = raw
    .split(/[\n,;]+/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  return [...new Set(parts)];
}

export function isCampaignEmailValid(email: string): boolean {
  const trimmed = email.trim().toLowerCase();
  return trimmed.length > 0 && CAMPAIGN_EMAIL_RE.test(trimmed);
}

/** A campaign recipient with an optional display name (sponsor invite). */
export interface CampaignRecipientInput {
  /** Optional recipient name as entered (may be a full name). */
  name?: string;
  /** Lower-cased, validated email address. */
  email: string;
}

const ANGLE_NAME_EMAIL_RE = /^(.*)<\s*([^>]+?)\s*>\s*$/;

/**
 * Parse a single bulk-add line into a recipient.
 * Supported formats (one recipient per line):
 *   - `Name <email@x.com>`
 *   - `Name, email@x.com`  /  `Name; email@x.com`  /  `Name\temail@x.com`
 *   - `email@x.com` (no name)
 * @returns the recipient, or null when the line has no valid email.
 */
export function parseCampaignRecipientLine(line: string): CampaignRecipientInput | null {
  const trimmed = line.trim();
  if (!trimmed) return null;

  // "Name <email>"
  const angle = ANGLE_NAME_EMAIL_RE.exec(trimmed);
  if (angle) {
    const email = angle[2].trim().toLowerCase();
    if (!CAMPAIGN_EMAIL_RE.test(email)) return null;
    const name = angle[1].replace(/[,;]\s*$/, '').trim();
    return name ? { name, email } : { email };
  }

  // "Name, email" / "Name; email" / "Name<TAB>email" / bare email
  const tokens = trimmed.split(/[,;\t]+/).map((t) => t.trim()).filter(Boolean);
  const emailIdx = tokens.findIndex((t) => CAMPAIGN_EMAIL_RE.test(t.toLowerCase()));
  if (emailIdx === -1) return null;
  const email = tokens[emailIdx].toLowerCase();
  const name = tokens.filter((_, i) => i !== emailIdx).join(' ').trim();
  return name ? { name, email } : { email };
}

/**
 * Parse a bulk-add textarea (one recipient per line) into recipients + invalid lines.
 * Deduplicates by email (first occurrence wins, keeping its name).
 */
export function parseCampaignRecipients(raw: string): {
  recipients: CampaignRecipientInput[];
  invalid: string[];
} {
  const recipients: CampaignRecipientInput[] = [];
  const invalid: string[] = [];
  const seen = new Set<string>();
  for (const line of raw.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const parsed = parseCampaignRecipientLine(trimmed);
    if (!parsed) {
      invalid.push(trimmed);
      continue;
    }
    if (seen.has(parsed.email)) continue;
    seen.add(parsed.email);
    recipients.push(parsed);
  }
  return { recipients, invalid };
}

/**
 * @returns Valid addresses and any tokens that are not valid emails.
 */
export function partitionCampaignEmails(raw: string): { valid: string[]; invalid: string[] } {
  const parts = parseCampaignEmailAddresses(raw);
  const valid: string[] = [];
  const invalid: string[] = [];
  for (const e of parts) {
    if (CAMPAIGN_EMAIL_RE.test(e)) valid.push(e);
    else invalid.push(e);
  }
  return { valid, invalid };
}
